import React, {useEffect, useState} from 'react';
import ReactGA from "react-ga4";
import {useParams} from 'react-router-dom';
import '../../style/Hostage.css';
import {Box, Button, Container, Typography} from "@mui/material";
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';
import ActivityForm from '../Activity/ActivityForm';
import UnsupportHostageDrawer from './UnsupportHostageDrawer';
import {Activity, Hostage, User} from '../types';
import NoActivitiesSVG from '../../icons/no-activities.svg';
import ActivityList from "../Activity/ActivityList";
import { decode } from '../../utils/Utils';

const HostageActivities = () => {
  const {t, i18n} = useTranslation();
  const {id} = useParams();
  const [hostage, setHostage] = useState<Hostage | null>(null);
  const [events, setEvents] = useState<Activity[]>([]);
  const [eventsHe, setEventsHe] = useState<Activity[]>([]);
  const [eventsEn, setEventsEn] = useState<Activity[]>([]);
  const [isAddActivityDrawerOpen, setIsAddActivityDrawerOpen] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [openConfirmDrawer, setOpenConfirmDrawer] = useState(false);
  const [registeredUsersMap, setRegisteredUsersMap] = useState<Record<string, number>>({});

  const navigate = useNavigate();

  useEffect(() => {
    // Fetch data based on the item id
    fetchHostageData();
    getHostagesEvents();
    initNumOfRegisteredUsers(events);
  }, [id]);

  if (!hostage) {
    return <div>{t('hostageCard.loading')}</div>; // Show a loading message while fetching data
  }

  if (!user) {
    return null;
  }

  function fetchHostageData() {
    fetch(`/api/hostage/${id}`, {
      credentials: 'include' // include cookies in the request
    })
      .then(response => response.json())
      .then(res => {
        setHostage(res.data);
        setUser(res.user);
      })
      .catch(err => {
        console.error('Error fetching hostage data:', err);
        console.log(`No data found for id: ${id}, redirecting to home`);
        navigate("/"); // Redirect to home if no data is found
      });
  }

  function getHostagesEvents() {
    fetch(`/api/activity/byHostage/${id}`, {
      credentials: 'include' // include cookies in the request
    })
      .then(response => response.json())
      .then(res => {
        const activities = res as Activity[];
        setEvents(activities);
        setEventsHe(activities.filter(x=>!!x?.he?.title));
        setEventsEn(activities.filter(x=>!!x?.en?.title));
        initNumOfRegisteredUsers(res);
      })
      .catch(err => {
        console.error('Error fetching hostage data:', err);
      });
  }

  function initNumOfRegisteredUsers(events: Activity[]) {
    const userCountMap: Record<string, number> = {};

    events.forEach((activity) => {
      if (activity.id) {
        userCountMap[activity.id] = activity.registeredUsersCount;
      }
    });

    setRegisteredUsersMap(userCountMap);
  }

  function canAddActivities(user: User, hostage: Hostage) {
    return user.role === 'admin' ||
      (user.role === 'moderator' && user.moderating?.includes(hostage.id));
  }

  const isJoined = user.supported_hostages.includes(hostage.id);
  const ENVIRONMENT = process.env.NODE_ENV || 'development';
  const SERVER_BASE_URL = ENVIRONMENT === 'development' ? 'http://localhost:3001' : '';


  const handleConfirmUnsupport = async () => {
    try {
      const response = await fetch(`${SERVER_BASE_URL}/api/user/unsupport`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({hostageId: id})
      });
      const res = await response.json();
      console.log('unSupport request successful:', res);

      ReactGA.event({
        category: "Event",
        action: "unSupport Hostage Clicked",
        label: id
      });
      setOpenConfirmDrawer(false); // Close the drawer after success
      window.location.reload();
    } catch (err) {
      console.error('Error sending unSupport request:', err);
    }
  };

  const handleUnsupportClick = () => {
    setOpenConfirmDrawer(true); // Open the confirmation dialog
  };



  const handleCreateActivity = () => {
    setIsAddActivityDrawerOpen(true);
  }


  return (
    <Container maxWidth="sm" className="hostage-card-container">

      { canAddActivities(user, hostage) ? <Button onClick={handleCreateActivity}>הוספת פעילות</Button> : null}
      <ActivityForm
        open={isAddActivityDrawerOpen}
        onClose={() => {
          setIsAddActivityDrawerOpen(false);
          getHostagesEvents();
        }}
        hostage={hostage}
      />

      {events.length === 0 ? (
        <>
          <Typography variant="body1" sx={{textAlign: 'center', fontWeight: 600}}>
            {decode(t('hostageCard.noEvents', {name: hostage[i18n.language as "en" | "he"].first_name}))}
          </Typography>
          <Typography variant="body2" sx={{textAlign: 'center', margin: '8px 30px 30px 30px'}}>
            {decode(t('hostageCard.noEventsSub', {name: hostage[i18n.language as "en" | "he"].first_name}))}
          </Typography>
        </>
      ) : (
        <>
          <Typography variant="body2" sx={{textAlign: 'right', margin: 2, fontSize: 16, fontWeight: 500}}>
            {t('hostageCard.joinText')}
          </Typography>
          <Typography
            variant="subtitle1"
            component="h2"
            className="user-activities-subtitle">
            {t("hostageCard.activitiesListTitle")}
          </Typography>
          <ActivityList
            activities={i18n.language == "he" ? eventsHe : eventsEn}
            hostages={[hostage]}
            registeredUsersMap = {registeredUsersMap}
            onRefresh={getHostagesEvents}
            onJoin={getHostagesEvents}
            onUndoJoin={getHostagesEvents}
          />
        </>
      )}
      { isJoined &&
        <>
          <Button
            variant="outlined"
            className="hostage-unsupport-btn"
            onClick={ handleUnsupportClick }>
            {t('hostageCard.unsupportBtn')}
          </Button>

          {/* Confirmation Dialog */}
          <UnsupportHostageDrawer
            open={openConfirmDrawer}
            onClose={() => setOpenConfirmDrawer(false)}
            onConfirm={handleConfirmUnsupport}
            hostageName={hostage[i18n.language as "en" | "he"].name_with_title}
          />
        </>
      }
    </Container>
  );
};

export default HostageActivities;
